import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatDialog, MatDialogRef, MatPaginator, MatSort, PageEvent} from '@angular/material';
import {SelectionModel} from '@angular/cdk/collections';
import {DecimalPipe} from '@angular/common';
import {ChangeColumnsComponent} from '../change-columns/change-columns.component';
import {ChangeCurrencyDialogComponent} from '../change-currency-dialog/change-currency-dialog.component';
import {TableColumnsService} from '../../services/table-columns.service';
import {CurrencyType, TableViewService} from './table-view.service';
import {TableDetailsService} from '../../services/table-details.service';
import {SortModel, SortOrderDialogComponent} from '../sort-order-dialog/sort-order-dialog.component';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {VpColumnFilterPipe} from '../../pipes/vp-column-filter.pipe';
import {FilterHeaderService} from '../../services/filter-header.service';
import {VerityTableDataSource} from './verity-table.data.source';
import {VerityPriceModel} from '../../models/verity-price-model';
import {CurrencyService} from '../../services/currency.service';
import {AuthService} from '../../services/auth.service';
import {Observable, Subject, Subscription} from 'rxjs';
import {VeritiyPricesTableRowModel} from '../../../prices-view/models/verity-prices-table-row-model';
import {ExcelExportService} from '../../services/excel-export.service';
import {VerityPriceTableStates} from 'src/app/prices-view/components/verity-prices-table/verity-prices-table-states';
import {TableChartState} from 'src/app/prices-view/components/verity-prices-table/table-chart-state';
import {LoadFiltersDialogComponent} from '../load-filters-dialog/load-filters-dialog.component';
import {SaveFiltersDialogComponent} from '../save-filters-dialog/save-filters-dialog.component';
import {Router} from '@angular/router';
import {TableChartsService} from './table-charts.service';
import {TableLayoutService} from '../../services/table-layout.service';

@Component({
  selector: 'app-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.css'],
  providers: [VpColumnFilterPipe]
})
export class TableViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selection = new SelectionModel<any>(true, []);
  @Input() showChangeView = true;
  @Input() showChangeCurrency = true;
  @Input() graphAvailable = true;
  @Input() persistFiltersAvailable = true;
  @Input() description = '';
  @Input() footnote = '';
  @Input() isChangelog: boolean;
  @Input() columns = new Array<VeritiyPricesTableRowModel>();

  @Input()
  set tableInputData(tableInputData: Array<any>) {
    if (tableInputData) {
      this.backupData = JSON.parse(JSON.stringify(tableInputData));
      this.dataSource.paginator = this.paginator;
      this.dataSource.data = this.currencyService.applyCurrency(tableInputData);

      this.onDynamicFilterChange(this.filterHeaderService.selectedFilterEntries);

      this.headerFilterEntries = this.filterHeaderService.generateFilterEntries(this.dataSource.data);
      this.tableService.setProperWidth(this.dataSource.data, this.columns);

      this.setDevModeColumns();

      this.tableLayoutService.loadFilterSettingsFromServer(this.router.url)
        .subscribe((result) => {
          const filterTypes = result['selectedFilterTypes'];
          const filterEntries = result['selectedFilterEntries'];

          this.filterHeaderService.selectedFilterTypes = filterTypes;
          this.filterHeaderService.selectedFilterEntries = filterEntries;

          this.loadFiltersEvent.emit(result);
          this.onDynamicFilterChange(this.filterHeaderService.selectedFilterEntries);

          this.initTableColumns();
        });
    }
  }

  @Output() columnsChange: EventEmitter<any> = new EventEmitter();
  @Output() tableViewChange = new EventEmitter<VerityPriceTableStates>();

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild('tableHeader', {static: true}) tableHeader: ElementRef;

  filterName: string;
  confirmDialog: MatDialogRef<ConfirmDialogComponent>;
  limitForChartAlertDialog = 50;
  tableState: VerityPriceTableStates = VerityPriceTableStates.EXTERNAL;
  displayedColumns: Array<string> = [];
  filterColumns: Array<string> = [];
  selectedSortFields: SortModel[] = [];

  chartInput = [];
  backupData = [];
  selectedCurrency: CurrencyType = CurrencyType.NONE;
  currencyRatio = [];
  lastUpdateDate: Date;
  dataSource = new VerityTableDataSource();

  // FILTER
  headerFilterEntries: any;
  currentlyEditingFilter: any;
  resetFiltersEvent = new EventEmitter();
  loadFiltersEvent = new EventEmitter<any>();

  // Subscriptions
  userTypeSubscription: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.initTableColumns();
    }
  }

  constructor(public filterHeaderService: FilterHeaderService,
              public tableService: TableViewService,
              private decimal: DecimalPipe,
              private dialog: MatDialog,
              private tableColService: TableColumnsService,
              private tableDetailsService: TableDetailsService,
              private exportService: ExcelExportService,
              private authService: AuthService,
              private currencyService: CurrencyService,
              private tableChartsService: TableChartsService,
              private router: Router,
              private tableLayoutService: TableLayoutService
  ) {

  }

  ngOnInit() {
    this.resetColumnsFromServer()
      .subscribe(() => {
        this.initTableColumns();
        this.addTableHeaderSortAndPaginator();

        this.tableColService.tableColumns = this.columns;
        this.dataSource.sortingDataAccessor = this.tableService.customSorting();
        this.dataSource.filterUpdate.subscribe(result => {
          this.exportService.currentTableData = result;

          if (this.tableService.tableChartState === TableChartState.CHART) {
            if (this.dataSource.filteredData.length <= this.limitForChartAlertDialog) {
              this.chartInput = this.tableChartsService.generateGraphData(this.dataSource.filteredData, this.displayedColumns, this.isChangelog);
            } else {
              this.openConfirmDialogForCharts();
            }
          }

          this.headerFilterEntries = this.filterHeaderService.generateFilterEntries(
            this.dataSource.filteredData,
            this.currentlyEditingFilter,
            this.headerFilterEntries);
        });
        this.dataSource.sortUpdate.subscribe((result: Array<VerityPriceModel>) => {
          this.exportService.currentTableData = result;
          this.tableDetailsService.sortData = result;
        });
      });
  }

  ngOnDestroy(): void {
    if (this.userTypeSubscription) {
      this.userTypeSubscription.unsubscribe();
    }
  }

  loadFilters() {
    const loadFiltersDialogRef = this.dialog.open(LoadFiltersDialogComponent, {});

    loadFiltersDialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.filterHeaderService.filterName = result['filterName'];
          const filterData = JSON.parse(result['filters']);

          this.loadFiltersEvent.emit(filterData);

          this.onDynamicFilterChange(this.filterHeaderService.selectedFilterEntries);
        }
      });
  }

  saveFilters() {
    const saveFilterDialogRef = this.dialog.open(SaveFiltersDialogComponent, {});
    saveFilterDialogRef.afterClosed()
      .subscribe(filterName => {
        this.filterHeaderService.filterName = filterName;
      });
  }

  initTableColumns() {
    this.displayedColumns = [];
    this.filterColumns = [];

    this.columns
      .filter(value => value.visible)
      .sort((a: VeritiyPricesTableRowModel, b: VeritiyPricesTableRowModel) => a.position - b.position)
      .forEach(value => {
        this.displayedColumns.push(value.key);
        this.filterColumns.push(value.key + '_filter');
      });

    if (this.tableService.tableChartState === TableChartState.CHART) {
      this.chartInput = this.tableChartsService.generateGraphData(this.dataSource.sortedData, this.displayedColumns, this.isChangelog);
    }
  }

  private resetColumnsFromServer(): Observable<any> {
    const subject = new Subject();

    this.tableLayoutService.loadColumnSettingsFromServer(this.router.url)
      .subscribe(result => {
          const storedColumns = result['columns'];
          const tableState = result['tableState'];

          if (tableState) {
            this.tableState = tableState;
          }

          if (storedColumns && storedColumns !== '') {
            storedColumns.forEach(col => {
              const veritiyPricesTableRowModel = this.columns.find(c => col['name'] === c.name);
              if (veritiyPricesTableRowModel) {
                veritiyPricesTableRowModel.visible = col['visible'];

              }
            });
          }

          subject.next();
        }, error => {
        }
        , () => subject.next());

    return subject.asObservable();
  }

  private setDevModeColumns() {
    if (this.authService.isTestUser === undefined) {
      this.userTypeSubscription = this.authService.isTestingUser().subscribe(isTestUser => {
        if (!isTestUser) {
          this.removeDevColumns();
        }
      });
    } else if (!this.authService.isTestUser) {
      this.removeDevColumns();
    }
  }

  private removeDevColumns() {
    const skuIdCol = this.displayedColumns.find(col => col === 'SKU_id');
    if (skuIdCol) {
      this.displayedColumns.splice(this.displayedColumns.indexOf(skuIdCol), 1);
    }

    const locProductCdCol = this.displayedColumns.find(col => col === 'Loc_Product_cd');
    if (locProductCdCol) {
      this.displayedColumns.splice(this.displayedColumns.indexOf(locProductCdCol), 1);
    }


    const skuId = this.columns.find(col => col.key === 'SKU_id');
    if (skuId) {
      this.columns.splice(this.columns.indexOf(skuId), 1);
    }

    const locProductCd = this.columns.find(col => col.key === 'Loc_Product_cd');
    if (locProductCd) {
      this.columns.splice(this.columns.indexOf(locProductCd), 1);
    }
  }

  private addTableHeaderSortAndPaginator() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onChangeCurrencyClick() {
    const countries = new Set();

    this.dataSource.filteredData.forEach(row => {
      countries.add(row.Country_name);
    });

    const currencyDialogResult = this.dialog.open(ChangeCurrencyDialogComponent, {
      data: countries,
      disableClose: true
    });
    currencyDialogResult.afterClosed().subscribe(result => {
      if (result && result.hasOwnProperty('option')) {
        this.selectedCurrency = result['option'];
        this.currencyRatio = result['data'];

        this.dataSource.data = this.currencyService.calculateCurrency(this.backupData, this.currencyRatio, this.selectedCurrency);
        this.chartInput = this.tableChartsService.generateGraphData(this.dataSource.sortedData, this.displayedColumns, this.isChangelog);
      }
    });
  }

  onChangeColumnsClick() {
    const dialogRef = this.dialog.open(ChangeColumnsComponent, {
      data: JSON.parse(JSON.stringify(this.columns)),
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: Array<VeritiyPricesTableRowModel>) => {
      if (result !== undefined) {

        this.displayedColumns = [];

        this.columns = result;
        result.forEach(column => {
          if (column.visible) {
            this.displayedColumns.push(column.key);
          }
        });

        this.tableLayoutService.saveColumnSettingsToServer(this.router.url, this.columns, this.tableState);
      }
    });
  }

  switchToTableView() {
    this.tableService.tableChartState = TableChartState.TABLE;
  }

  switchToChartView() {

    if (this.dataSource.filteredData.length >= this.limitForChartAlertDialog) {
      this.openConfirmDialogForCharts();

    } else {
      this.tableService.tableChartState = TableChartState.CHART;
      this.chartInput = this.tableChartsService.generateGraphData(this.dataSource.sortedData, this.displayedColumns, this.isChangelog);
    }
  }

  private openConfirmDialogForCharts() {
    if (!this.confirmDialog) {
      this.confirmDialog = this.dialog.open(ConfirmDialogComponent);
      this.confirmDialog.afterClosed()
        .subscribe(result => {
          if (result) {
            this.tableService.tableChartState = TableChartState.CHART;
            this.chartInput = this.tableChartsService.generateGraphData(this.dataSource.sortedData, this.displayedColumns, this.isChangelog);
          } else {
            this.tableService.tableChartState = TableChartState.TABLE;
          }

          this.confirmDialog = undefined;
        });
    }
  }

  onExternalViewClick() {
    this.tableState = VerityPriceTableStates.EXTERNAL;
    this.tableViewChange.emit(this.tableState);
    this.tableLayoutService.saveColumnSettingsToServer(this.router.url, this.columns, this.tableState);
    this.initTableColumns();
  }

  onInternalViewClick() {
    this.tableState = VerityPriceTableStates.INTERNAL;
    this.tableViewChange.emit(this.tableState);
    this.tableLayoutService.saveColumnSettingsToServer(this.router.url, this.columns, this.tableState);
    this.initTableColumns();
  }

  onPaginatorChange($event: PageEvent) {
    this.tableDetailsService.pagerSize = $event['pageSize'];
  }

  getPageSize(): number {
    return this.tableDetailsService.pagerSize;
  }

  onMultiSortClicked() {
    const matDialogRef = this.dialog.open(SortOrderDialogComponent, {
      width: '500px',
      data: {
        tableColumns: JSON.parse(JSON.stringify(this.columns)),
        sortedColumns: this.selectedSortFields
      },
      disableClose: true
    });

    matDialogRef.afterClosed()
      .subscribe((result: Array<SortModel>) => {
        if (result) {
          this.sort.active = undefined;
          this.sort.ngOnChanges();
          this.selectedSortFields = result;
          const sortedData = this.tableService.performGroupedSort(result, this.dataSource.data);

          this.dataSource.data = JSON.parse(JSON.stringify(sortedData));
        }
      });
  }

  onActualFilterChange($event: string) {
    this.currentlyEditingFilter = $event;
  }

  onFilterTypeHasNoneSelected($event: Array<string>) {
    $event.forEach(key => {
      this.filterHeaderService.addAllEntriesToFilter(key, this.headerFilterEntries, this.dataSource.filteredData);
    });
  }

  onDynamicFilterChange(filterValue) {
    const resultFilter = {};

    Object.keys(filterValue).forEach(key => {
      resultFilter[key] = [];

      const filterValuesArray = filterValue[key];
      filterValuesArray.forEach(fv => {
        const foundRow = this.dataSource.data.find(row => row[key] === fv);
        if (foundRow) {
          resultFilter[key].push(fv)
        }
      });

      if (resultFilter[key].length === 0) {
        delete resultFilter[key];
      }
    });

    this.dataSource.filter = resultFilter;
  }

  resetFilters() {
    this.filterHeaderService.filterName = '';
    this.resetFiltersEvent.next();
  }
}
